/**
 * Chart Data Generators
 *
 * Transforms SystemRecOutput into chart-ready data structures
 * for frontend visualization libraries (Chart.js, Recharts, D3, etc.)
 *
 * These generators are library-agnostic — they produce clean data structures
 * that can be consumed by any charting library.
 *
 * IMPORTANT: All series must have unique IDs. Duplicate IDs cause datasets
 * to overwrite each other, resulting in only one line being drawn.
 */

import type { SystemRecOutput, RecommendationOption, DecisionScenario } from './types';
import type { BaselineResult } from './baseline';
import { calculateCarbonSavingsForRecommendations, type CarbonSavingsComparison } from './carbonEmissions';

// ==================== UTILITY FUNCTIONS ====================

/**
 * Validate that all series have unique IDs
 * Logs warning if duplicates found and returns deduplicated array
 */
function validateAndDeduplicateSeries<T extends { id: string }>(
  series: T[],
  chartName: string
): T[] {
  const seenIds = new Set<string>();
  const duplicates: string[] = [];
  const deduplicated: T[] = [];

  for (const s of series) {
    if (seenIds.has(s.id)) {
      duplicates.push(s.id);
      // Skip duplicate - keep first occurrence
    } else {
      seenIds.add(s.id);
      deduplicated.push(s);
    }
  }

  if (duplicates.length > 0) {
    console.warn(
      `[${chartName}] Duplicate series IDs detected and removed: ${duplicates.join(', ')}. ` +
      `This may indicate an issue with option ID generation.`
    );
  }

  return deduplicated;
}

// ==================== CUMULATIVE COST OVER TIME ====================

export interface CostOverTimeDataPoint {
  year: number;
  cost: number;
}

export interface CostOverTimeSeries {
  id: string;
  label: string;
  color: string;
  data: CostOverTimeDataPoint[];
  isBaseline?: boolean;
}

export interface CostOverTimeChartData {
  series: CostOverTimeSeries[];
  metadata: {
    periodYears: number;
    currencySymbol: string;
    yAxisLabel: string;
    xAxisLabel: string;
  };
}

/**
 * Generate cumulative cost over time data
 *
 * This is the MOST IMPORTANT chart — it shows:
 * - Break-even points
 * - Long-term divergence
 * - Cost crossovers
 *
 * Y-axis: Cumulative £
 * X-axis: Years (0 to periodYears)
 */
export function generateCostOverTimeChart(
  output: SystemRecOutput,
  periodYears?: number
): CostOverTimeChartData | null {
  if (!output.baselineResult) {
    return null; // Cannot generate without baseline
  }

  const period = periodYears || 20;
  const series: CostOverTimeSeries[] = [];

  // Color palette (consulting deck style)
  const colors = {
    baseline: '#94a3b8',  // slate-400 (neutral baseline)
    primary: '#3b82f6',   // blue-500 (recommended option)
    alt1: '#10b981',      // green-500 (alternative 1)
    alt2: '#f59e0b',      // amber-500 (alternative 2)
    alt3: '#ef4444',      // red-500 (alternative 3)
  };

  // 1. Baseline series
  const baselineSeries = generateBaselineSeries(
    output.baselineResult,
    period,
    colors.baseline
  );
  series.push(baselineSeries);

  // 2. Primary recommendation
  const primarySeries = generateOptionSeries(
    output.primary,
    output.primary.id,  // Use option.id instead of synthetic 'primary'
    output.primary.title,
    period,
    colors.primary
  );
  if (primarySeries) {
    series.push(primarySeries);
  }

  // 3. Alternatives - use option.id to prevent duplicates, limit to 3 for chart clarity
  const altColors = [colors.alt1, colors.alt2, colors.alt3];
  const alts = output.alternatives.slice(0, 3);
  alts.forEach((alt, index) => {
    const altSeries = generateOptionSeries(
      alt,
      alt.id,  // Use option.id instead of synthetic 'alt1', 'alt2', etc.
      alt.title,
      period,
      altColors[index] || colors.alt3
    );
    if (altSeries) {
      series.push(altSeries);
    }
  });

  // Validate and deduplicate series to prevent overwriting
  const validatedSeries = validateAndDeduplicateSeries(series, 'CostOverTimeChart');

  return {
    series: validatedSeries,
    metadata: {
      periodYears: period,
      currencySymbol: '£',
      yAxisLabel: 'Cumulative Cost (£)',
      xAxisLabel: 'Years',
    },
  };
}

/**
 * Generate cumulative cost series for baseline
 */
function generateBaselineSeries(
  baseline: BaselineResult,
  periodYears: number,
  color: string
): CostOverTimeSeries {
  const data: CostOverTimeDataPoint[] = [];

  // Year 0: starting point
  data.push({ year: 0, cost: 0 });

  // Calculate cumulative costs year by year
  let cumulative = 0;
  for (let year = 1; year <= periodYears; year++) {
    const yearData = baseline.yearlyBreakdown[year - 1];
    if (yearData) {
      cumulative += yearData.totalAnnualCost_gbp;
    } else {
      // If we don't have yearly data, use annualized cost
      cumulative += baseline.annualizedCost_gbp;
    }
    data.push({ year, cost: Math.round(cumulative) });
  }

  return {
    id: 'baseline',
    label: 'Do Nothing (Current System)',
    color,
    data,
    isBaseline: true,
  };
}

/**
 * Generate cumulative cost series for an option
 */
function generateOptionSeries(
  option: RecommendationOption,
  id: string,
  label: string,
  periodYears: number,
  color: string
): CostOverTimeSeries | null {
  if (!option.lcoo) {
    return null; // Cannot generate without LCOO data
  }

  const data: CostOverTimeDataPoint[] = [];
  const lcoo = option.lcoo;

  // Year 0: upfront cost
  data.push({
    year: 0,
    cost: Math.round(lcoo.upfrontNetGBP || lcoo.upfrontGrossGBP || 0),
  });

  // Calculate cumulative costs year by year
  let cumulative = lcoo.upfrontNetGBP || lcoo.upfrontGrossGBP || 0;

  const annualRunning = lcoo.annualRunningGBP || 0;
  const annualMaintenance = lcoo.annualMaintenanceGBP || 0;
  const annualTotal = annualRunning + annualMaintenance;

  // If we have replacement data, factor that in
  const replacements = option.longevity?.replacementsInPeriod || 0;
  const avgLifespan =
    option.longevity
      ? (option.longevity.expectedLifespanYears.min + option.longevity.expectedLifespanYears.max) / 2
      : 15;

  for (let year = 1; year <= periodYears; year++) {
    cumulative += annualTotal;

    // Add replacement cost if this is a replacement year
    if (replacements > 0 && year % Math.round(avgLifespan) === 0) {
      const replacementCost = (lcoo.replacementCostsGBP || 0) / replacements;
      cumulative += replacementCost;
    }

    data.push({ year, cost: Math.round(cumulative) });
  }

  return {
    id,
    label,
    color,
    data,
  };
}

// ==================== ANNUAL COST COMPARISON ====================

export interface AnnualCostBar {
  id: string;
  label: string;
  annualCost_gbp: number;
  deltaVsBaseline_gbp: number | null;
  color: string;
  isBaseline?: boolean;
}

export interface AnnualCostChartData {
  bars: AnnualCostBar[];
  baselineValue: number;
  metadata: {
    currencySymbol: string;
    yAxisLabel: string;
  };
}

/**
 * Generate annual cost comparison bar chart data
 *
 * Shows:
 * - Do nothing
 * - Primary
 * - Alternatives
 *
 * With delta annotations: "£X lower/higher than baseline"
 */
export function generateAnnualCostChart(
  output: SystemRecOutput
): AnnualCostChartData | null {
  if (!output.baselineResult) {
    return null;
  }

  const bars: AnnualCostBar[] = [];
  const baselineValue = output.baselineResult.annualizedCost_gbp;

  const colors = {
    baseline: '#94a3b8',
    primary: '#3b82f6',
    alt1: '#10b981',
    alt2: '#f59e0b',
    alt3: '#ef4444',
  };

  // 1. Baseline bar
  bars.push({
    id: 'baseline',
    label: 'Do Nothing',
    annualCost_gbp: baselineValue,
    deltaVsBaseline_gbp: null,
    color: colors.baseline,
    isBaseline: true,
  });

  // 2. Primary bar
  if (output.primary.deltaVsBaseline) {
    bars.push({
      id: output.primary.id,  // Use option.id
      label: output.primary.boilerLabel,
      annualCost_gbp: output.primary.deltaVsBaseline.optionAnnualized_gbp,
      deltaVsBaseline_gbp: output.primary.deltaVsBaseline.annualSaving_gbp,
      color: colors.primary,
    });
  }

  // 3. Alternative bars - use option.id to prevent duplicates, limit to 3 for chart clarity
  const altColors = [colors.alt1, colors.alt2, colors.alt3];
  const alts = output.alternatives.slice(0, 3);
  alts.forEach((alt, index) => {
    if (alt.deltaVsBaseline) {
      bars.push({
        id: alt.id,  // Use option.id instead of synthetic 'alt1', etc.
        label: alt.boilerLabel,
        annualCost_gbp: alt.deltaVsBaseline.optionAnnualized_gbp,
        deltaVsBaseline_gbp: alt.deltaVsBaseline.annualSaving_gbp,
        color: altColors[index] || colors.alt3,
      });
    }
  });

  return {
    bars,
    baselineValue,
    metadata: {
      currencySymbol: '£',
      yAxisLabel: 'Annual Cost (£/year)',
    },
  };
}

// ==================== LONGEVITY TIMELINE ====================

export interface LongevityTimelineBar {
  id: string;
  label: string;
  lifespanYears: { min: number; max: number };
  replacements: number;
  color: string;
  reasoning: string;
}

export interface LongevityTimelineChartData {
  bars: LongevityTimelineBar[];
  maxYears: number;
  metadata: {
    xAxisLabel: string;
    replacementMarkerLabel: string;
  };
}

/**
 * Generate longevity timeline (horizontal bars with replacement markers)
 *
 * Shows:
 * |–– 11y ––| replace |–– 11y ––|
 * |———————–– 22y ———————––|
 *
 * Makes lifespan intuitive and visual
 */
export function generateLongevityTimelineChart(
  output: SystemRecOutput,
  periodYears?: number
): LongevityTimelineChartData {
  const period = periodYears || 20;
  const bars: LongevityTimelineBar[] = [];

  const colors = {
    baseline: '#94a3b8',
    primary: '#3b82f6',
    alt1: '#10b981',
    alt2: '#f59e0b',
    alt3: '#ef4444',
  };

  // 1. Baseline (if we have existing system data)
  if (output.baselineResult) {
    const existingAge = output.inputEcho.existingSystemAge || 0;
    bars.push({
      id: 'baseline',
      label: 'Current System',
      lifespanYears: { min: 8, max: 12 },
      replacements: existingAge > 8 ? 2 : 1, // Estimate
      color: colors.baseline,
      reasoning: 'Already aged, unknown condition',
    });
  }

  // 2. Primary
  if (output.primary.longevity) {
    bars.push({
      id: output.primary.id,  // Use option.id
      label: output.primary.boilerLabel,
      lifespanYears: output.primary.longevity.expectedLifespanYears,
      replacements: output.primary.longevity.replacementsInPeriod,
      color: colors.primary,
      reasoning: output.primary.longevity.reasoning,
    });
  }

  // 3. Alternatives - use option.id to prevent duplicates, limit to 3 for chart clarity
  const altColors = [colors.alt1, colors.alt2, colors.alt3];
  const alts = output.alternatives.slice(0, 3);
  alts.forEach((alt, index) => {
    if (alt.longevity) {
      bars.push({
        id: alt.id,  // Use option.id instead of synthetic 'alt1', etc.
        label: alt.boilerLabel,
        lifespanYears: alt.longevity.expectedLifespanYears,
        replacements: alt.longevity.replacementsInPeriod,
        color: altColors[index] || colors.alt3,
        reasoning: alt.longevity.reasoning,
      });
    }
  });

  return {
    bars,
    maxYears: period,
    metadata: {
      xAxisLabel: 'Years',
      replacementMarkerLabel: 'Replacement',
    },
  };
}

// ==================== SUMMARY CARDS DATA ====================

export interface SummaryCard {
  id: string;
  label: string;
  value: string;
  subtext?: string;
  delta?: string;
  trend?: 'positive' | 'negative' | 'neutral';
  color: string;
}

export interface SummaryCardsData {
  baseline: SummaryCard;
  primary: SummaryCard;
  alternatives: SummaryCard[];
}

/**
 * Generate summary card data for quick comparison
 *
 * These are the big numbers at the top of each section
 */
export function generateSummaryCards(
  output: SystemRecOutput,
  periodYears?: number
): SummaryCardsData {
  const period = periodYears || 20;

  const colors = {
    baseline: '#94a3b8',
    primary: '#3b82f6',
    alt1: '#10b981',
    alt2: '#f59e0b',
  };

  // Baseline card
  const baselineCard: SummaryCard = {
    id: 'baseline',
    label: 'Do Nothing',
    value: output.baselineResult
      ? `£${Math.round(output.baselineResult.annualizedCost_gbp).toLocaleString()}/yr`
      : 'N/A',
    subtext: output.baselineResult
      ? `£${Math.round(output.baselineResult.totalCost_gbp).toLocaleString()} over ${period} years`
      : undefined,
    color: colors.baseline,
    trend: 'neutral',
  };

  // Primary card
  const primaryDelta = output.primary.deltaVsBaseline;
  const primaryCard: SummaryCard = {
    id: 'primary',
    label: output.primary.boilerLabel,
    value: primaryDelta
      ? `£${Math.round(primaryDelta.optionAnnualized_gbp).toLocaleString()}/yr`
      : 'N/A',
    subtext: output.primary.lcoo
      ? `£${Math.round(output.primary.lcoo.totalGBP).toLocaleString()} over ${period} years`
      : undefined,
    delta: primaryDelta
      ? `${primaryDelta.annualSaving_gbp >= 0 ? '−' : '+'}£${Math.abs(Math.round(primaryDelta.annualSaving_gbp)).toLocaleString()}/yr vs baseline`
      : undefined,
    trend: primaryDelta && primaryDelta.annualSaving_gbp >= 0 ? 'positive' : 'negative',
    color: colors.primary,
  };

  // Alternative cards - limit to 3 for display clarity
  const altCards: SummaryCard[] = [];
  const altColors = [colors.alt1, colors.alt2, colors.alt2];

  const alts = output.alternatives.slice(0, 3);
  alts.forEach((alt, index) => {
    const altDelta = alt.deltaVsBaseline;
    altCards.push({
      id: `alt${index + 1}`,
      label: alt.boilerLabel,
      value: altDelta
        ? `£${Math.round(altDelta.optionAnnualized_gbp).toLocaleString()}/yr`
        : 'N/A',
      subtext: alt.lcoo
        ? `£${Math.round(alt.lcoo.totalGBP).toLocaleString()} over ${period} years`
        : undefined,
      delta: altDelta
        ? `${altDelta.annualSaving_gbp >= 0 ? '−' : '+'}£${Math.abs(Math.round(altDelta.annualSaving_gbp)).toLocaleString()}/yr vs baseline`
        : undefined,
      trend: altDelta && altDelta.annualSaving_gbp >= 0 ? 'positive' : 'negative',
      color: altColors[index] || colors.alt2,
    });
  });

  return {
    baseline: baselineCard,
    primary: primaryCard,
    alternatives: altCards,
  };
}

// ==================== CARBON SAVINGS CHART ====================

export interface CarbonSavingsDataPoint {
  year: number;
  saving_kgCO2: number;
  cumulativeSaving_kgCO2: number;
  // Uncertainty bounds for confidence intervals
  uncertainty?: number;  // 0-1 scale (e.g., 0.15 = ±15%)
  lowBound_kgCO2?: number;
  highBound_kgCO2?: number;
}

export interface CarbonSavingsSeries {
  id: string;
  label: string;
  color: string;
  data: CarbonSavingsDataPoint[];
  totalSaving_tonnes: number;
  percentReduction: number;
  // Overall uncertainty for this series
  uncertainty?: number;  // 0-1 scale (e.g., 0.15 = ±15%)
  confidence?: 'high' | 'medium' | 'low';
}

export interface CarbonSavingsChartData {
  series: CarbonSavingsSeries[];
  metadata: {
    periodYears: number;
    yAxisLabel: string;
    xAxisLabel: string;
    unit: string;
  };
}

/**
 * Generate carbon savings chart data
 * 
 * Shows cumulative carbon savings (in tonnes CO2) over time for each option
 * compared to baseline (keeping current system).
 * 
 * This provides evidence-based data on ongoing carbon reduction.
 */
export function generateCarbonSavingsChart(
  output: SystemRecOutput,
  periodYears?: number
): CarbonSavingsChartData | null {
  if (!output.baselineResult) {
    return null; // Cannot calculate savings without baseline
  }

  const period = periodYears || 20;
  const series: CarbonSavingsSeries[] = [];

  // Color palette
  const colors = {
    primary: '#10b981',   // green-500 (environmental benefit)
    alt1: '#3b82f6',      // blue-500 (alternative 1)
    alt2: '#f59e0b',      // amber-500 (alternative 2)
    alt3: '#ef4444',      // red-500 (alternative 3)
  };

  // Calculate carbon savings for all options
  const carbonSavings = calculateCarbonSavingsForRecommendations(output, period);

  // 1. Primary recommendation
  const primarySavings = carbonSavings.get(output.primary.id);
  if (primarySavings) {
    const primarySeries = generateCarbonSavingsSeries(
      output.primary,
      primarySavings,
      output.primary.id,  // Use option.id
      colors.primary
    );
    if (primarySeries) {
      series.push(primarySeries);
    }
  }

  // 2. Alternatives - use option.id to prevent duplicates, limit to 3 for chart clarity
  const altColors = [colors.alt1, colors.alt2, colors.alt3];
  const alts = (output.alternatives || []).slice(0, 3);
  alts.forEach((alt, index) => {
    const altSavings = carbonSavings.get(alt.id);
    if (altSavings) {
      const altSeries = generateCarbonSavingsSeries(
        alt,
        altSavings,
        alt.id,  // Use option.id instead of synthetic 'alt1', etc.
        altColors[index] || colors.alt3
      );
      if (altSeries) {
        series.push(altSeries);
      }
    }
  });

  // Return data if we have at least one series (should always be true if we have evaluated options)
  if (series.length === 0) {
    return null;
  }

  // Validate and deduplicate series to prevent overwriting
  const validatedSeries = validateAndDeduplicateSeries(series, 'CarbonSavingsChart');

  return {
    series: validatedSeries,
    metadata: {
      periodYears: period,
      yAxisLabel: 'Cumulative Carbon Savings (tonnes CO₂)',
      xAxisLabel: 'Years',
      unit: 'tonnes CO₂',
    },
  };
}

/**
 * Calculate uncertainty factor based on confidence level and system characteristics
 * 
 * Baseline systems (old, degraded) have wider error bars due to:
 * - Estimated sludge levels
 * - Unknown degradation
 * - Variable efficiency
 * 
 * Modern ErP systems have narrower error bars due to:
 * - Tightly regulated manufacturing standards
 * - Known, consistent performance
 * 
 * @param confidence - Confidence level from recommendation
 * @param isBaseline - Whether this is baseline "do nothing" scenario
 * @returns Uncertainty factor (0-1 scale, e.g. 0.15 = ±15%)
 */
function calculateUncertainty(
  confidence: 'high' | 'medium' | 'low',
  isBaseline: boolean
): number {
  // Baseline systems have higher uncertainty due to aging, sludge, degradation
  if (isBaseline) {
    return 0.25; // ±25% for aging systems with estimated conditions
  }
  
  // Modern systems with ErP ratings have much lower uncertainty
  switch (confidence) {
    case 'high':
      return 0.08;  // ±8% for well-specified systems (e.g., specific GC lookup)
    case 'medium':
      return 0.12;  // ±12% for systems with some assumptions
    case 'low':
      return 0.18;  // ±18% for systems with significant assumptions
    default:
      return 0.12;
  }
}

/**
 * Generate carbon savings series for a single option
 */
function generateCarbonSavingsSeries(
  option: RecommendationOption,
  savings: CarbonSavingsComparison,
  id: string,
  color: string,
  isBaseline: boolean = false
): CarbonSavingsSeries | null {
  // Calculate uncertainty based on confidence level
  const uncertainty = calculateUncertainty(option.confidence, isBaseline);
  
  // Include all evaluated options in comparison graphs, not just those with positive savings
  // This ensures system boilers, storage combis, and baseline all appear
  const data: CarbonSavingsDataPoint[] = savings.yearlyComparison.map(year => {
    const baseValue = year.cumulativeSaving_kgCO2;
    const absoluteUncertainty = Math.abs(baseValue) * uncertainty;
    
    return {
      year: year.year,
      saving_kgCO2: year.saving_kgCO2,
      cumulativeSaving_kgCO2: baseValue,
      uncertainty,
      // For positive savings, low bound is less, high bound is more
      // For negative savings (increased emissions), low bound is more negative, high bound is less negative
      lowBound_kgCO2: baseValue - absoluteUncertainty,
      highBound_kgCO2: baseValue + absoluteUncertainty,
    };
  });

  return {
    id,
    label: `${option.boilerLabel}`,
    color,
    data,
    totalSaving_tonnes: savings.totalSaving_tonnes,
    percentReduction: savings.percentReduction,
    uncertainty,
    confidence: option.confidence,
  };
}

// ==================== DECISION SCENARIOS ====================

/**
 * Determine technology type from boiler and water configuration
 */
function determineTechnology(boiler: string, water: string): 'gas' | 'heat-pump' | 'hybrid' {
  if (boiler === 'air_source_hp' || boiler === 'ground_source_hp') {
    return 'heat-pump';
  }
  if (boiler === 'hybrid') {
    return 'hybrid';
  }
  return 'gas';
}

/**
 * Build decision scenarios from recommendation output
 * This creates a unified array of scenarios that includes:
 * - Do-nothing baseline (scenarios[0])
 * - Each considered replacement option from primary + alternatives
 *
 * This is the single source of truth for charts, tables, and narrative.
 * Charts should ONLY render scenarios where considered=true.
 *
 * @param output - SystemRecOutput from computeSystemRecommendation
 * @returns Array of DecisionScenario objects
 */
export function buildDecisionScenarios(
  output: SystemRecOutput
): DecisionScenario[] {
  const scenarios: DecisionScenario[] = [];

  // 1. Add do-nothing baseline scenario
  if (output.baselineResult) {
    const baseline = output.baselineResult as BaselineResult;
    // Validate that baseline has expected properties
    if (baseline.annualizedCost_gbp !== undefined && baseline.totalCost_gbp !== undefined) {
      scenarios.push({
        id: 'baseline',
        label: 'Do Nothing (Current System)',
        kind: 'do-nothing',
        considered: true, // Baseline is always considered
        lcoo: {
          annualizedCostGBP: baseline.annualizedCost_gbp,
          totalCostGBP: baseline.totalCost_gbp,
          periodYears: baseline.periodYears,
        },
        annualCost: baseline.annualizedCost_gbp,
        lifetimeCost: baseline.totalCost_gbp,
        upfrontCost: 0, // Do nothing has no upfront cost
        assumptions: baseline.assumptions || [],
      });
    }
  }

  // 2. Add primary recommendation scenario
  if (output.primary) {
    const opt = output.primary;
    scenarios.push({
      id: 'primary',
      label: `${opt.boilerLabel} + ${opt.waterLabel}`,
      kind: 'replacement',
      considered: true,
      optionKey: opt.id,
      tech: determineTechnology(opt.boiler, opt.water),
      systemType: opt.systemType,
      lcoo: opt.lcoo,
      annualCost: opt.lcoo?.annualizedCostGBP,
      lifetimeCost: opt.lcoo?.totalCostGBP,
      upfrontCost: opt.lcoo?.upfrontNetGBP,
      assumptions: opt.lcoo?.assumptions,
      option: opt,
    });
  }

  // 3. Add alternative recommendation scenarios
  output.alternatives.forEach((alt, index) => {
    scenarios.push({
      id: `alt${index + 1}`,
      label: `${alt.boilerLabel} + ${alt.waterLabel}`,
      kind: 'replacement',
      considered: true,
      optionKey: alt.id,
      tech: determineTechnology(alt.boiler, alt.water),
      systemType: alt.systemType,
      lcoo: alt.lcoo,
      annualCost: alt.lcoo?.annualizedCostGBP,
      lifetimeCost: alt.lcoo?.totalCostGBP,
      upfrontCost: alt.lcoo?.upfrontNetGBP,
      assumptions: alt.lcoo?.assumptions,
      option: alt,
    });
  });

  return scenarios;
}

// ==================== EXPORT ALL CHART DATA ====================

export interface AllChartsData {
  costOverTime: CostOverTimeChartData | null;
  annualCost: AnnualCostChartData | null;
  longevityTimeline: LongevityTimelineChartData;
  summaryCards: SummaryCardsData;
  carbonSavings: CarbonSavingsChartData | null;
}

/**
 * Generate all chart data at once
 *
 * This is the main function you'll call to get all visualization data
 */
export function generateAllCharts(
  output: SystemRecOutput,
  periodYears?: number
): AllChartsData {
  const period = periodYears || 20;

  return {
    costOverTime: generateCostOverTimeChart(output, period),
    annualCost: generateAnnualCostChart(output),
    longevityTimeline: generateLongevityTimelineChart(output, period),
    summaryCards: generateSummaryCards(output, period),
    carbonSavings: generateCarbonSavingsChart(output, period),
  };
}
